/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, Component, DestroyRef, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {NlrFormFieldHasErrorPipe} from '../../../common/pipe/nlr-form-field-has-error.pipe';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {MapService} from '../../services/map.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {NgEventBus} from 'ng-event-bus';
import {DataFrameService} from '../../../common/service/data-frame-service';
import {EnlargementService} from '../enlargement/services/enlargement.service';
import {MapToolsService} from '../../services/map-tools.service';
import {first, Observable, of, tap} from 'rxjs';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {LookupService} from '../../../common/service/lookup.service';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {PermissionsService} from '../../../common/service/permissions.service';
import {WidgetService} from '../../services/widget.service';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';

@Component({
    selector: 'nlr-print-area',
    imports: [CommonModule, NlrFormFieldHasErrorPipe, FormsModule, CdkDrag, CdkDragHandle, HelpLinkComponent, NgSelectModule],
    templateUrl: './print-area.component.html',
    styleUrl: './print-area.component.scss'
})
export class PrintAreaComponent implements OnInit, OnDestroy, AfterViewInit {
    title = 'PrintAreaComponent';

    widgetId = 'printArea';

    widgetConfig: WidgetConfigLookup;

    paperSizeId: number;

    referenceScaleId: number;

    private permissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    private adjustFeatures: boolean;

    private coordSystemId: number;

    private editToolbar: any;

    private extent: any;

    private mapGraphicsClearEvent: any;

    private mapGraphicsClickEvent: any;

    private printAreaGraphic: any;

    private reapplyHighlightsTimeout: number;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'print-area-help.html';

    helpPageTitle: string = 'Set Print Area Tool';

    constructor(
        private dataFrameService: DataFrameService,
        private destroyRef: DestroyRef,
        private enlargementService: EnlargementService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private printAreaService: PrintAreaService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService
    ) {}

    ngOnInit() {
        this.mapService.map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());

        this.editToolbar = new this.esriModuleService.Edit(this.mapService.map);

        this.mapGraphicsClickEvent = this.mapService.map.graphics.on('click', (evt: any) => {
            this.editToolbar.activate(this.esriModuleService.Edit.MOVE, evt.graphic, {});
        });

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);

        this.coordSystemId = this.planModelService.getModel().spatialSettings.projectedCoordSystem.id;

        this.paperSizeId = this.printAreaService.getPaperSizeLookupId();

        this.adjustFeatures = true;

        if (this.printAreaService.isPrintAreaReferenceScaleIdValid()) {
            this.referenceScaleId = this.printAreaService.getReferenceScaleLookupId();
        } else {
            // NLRP19-586 update legacy unsupported plan scale to prevent feature adjustment
            if (this.printAreaService.getPrintAreaScaleId()) {
                this.adjustFeatures = false;
                this.referenceScaleId = this.getNextValidScaleId(this.printAreaService.getPrintAreaScaleId());
            } else {
                this.referenceScaleId = this.printAreaService.getDefaultReferenceScale().id;
            }
        }

        let observable: Observable<any>;
        const printAreaGeometry = this.printAreaService.getPrintAreaGeometry();
        if (printAreaGeometry) {
            this.updatePrintArea(printAreaGeometry.getExtent(), false);
            observable = of(null);
        } else {
            observable = this.setPrintAreaToDefaultExtent(!this.printAreaService.getPrintAreaFeature() && this.adjustFeatures);
        }

        observable
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => this.widgetService.setupWidget(this.widgetId))
            )
            .subscribe();

        this.eventBus
            .on('deactivatePrintArea')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.debug(`${this.title} - on - deactivatePrintArea`);
                    this.onClickCancel();
                })
            )
            .subscribe();

        this.eventBus
            .on('resetActionBehaviours')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    console.debug(`${this.title} - on - resetActionBehaviours`);
                    this.onClickCancel();
                })
            )
            .subscribe();
    }

    ngAfterViewInit() {
        this.widgetService.setupWidget(this.widgetId);
    }

    ngOnDestroy() {
        if (this.printAreaGraphic) {
            this.spatialUtilService.removeMapGraphic(this.printAreaGraphic);
        }
        if (this.mapGraphicsClickEvent) {
            this.mapGraphicsClickEvent.remove();
        }
        if (this.mapGraphicsClearEvent) {
            this.mapGraphicsClearEvent.remove();
        }
        this.editToolbar.deactivate();

        this.mapService.resetMapClickHandler();

        this.mapToolsService.deactivate('PRINT_AREA_WINDOW');
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    gotPrerequisites() {
        return this.paperSizeId && this.referenceScaleId && this.extent;
    }

    getPaperSizeLookups() {
        return this.printAreaService.getPaperSizeLookups();
    }

    isPaperSizeDisabled() {
        return this.permissionsService.isDisabledByPermissions(this.permissions);
    }

    getReferenceScaleLookups() {
        return this.printAreaService.getReferenceScaleLookups();
    }

    isReferenceScaleDisabled() {
        return this.permissionsService.isDisabledByPermissions(this.permissions);
    }

    onClickApply() {
        const printAreaFeature = {
            geometry: this.printAreaGraphic.geometry,
            attributes: {
                guid: this.planModelService.getGuid(),
                id: 1,
                type: 1,
                papersize: this.paperSizeId,
                scale: this.referenceScaleId
            }
        };

        const oldPaperSizeLookupId = this.printAreaService.getPaperSizeLookupId();

        const newReferenceScaleLookup = this.printAreaService.getReferenceScaleLookups().find(l => l.id === this.referenceScaleId);
        const scaleRatio = newReferenceScaleLookup.divisor / this.printAreaService.getReferenceScaleDivisor();

        const features = this.spatialUtilService.getPrintAreas();
        this.spatialUtilService
            .applyEdits('printArea', [printAreaFeature], null, features.length ? features : null)
            .pipe(
                tap(() => {
                    this.spatialUtilService.getPrintAreaMapLayer().refresh();

                    // Reapply highlights after print area changed
                    const featureHighlightsMap = structuredClone(this.spatialUtilService.getFeatureHighlightsMap());
                    const featureCount = this.spatialUtilService.getFeatureCount();

                    const reapplyHighlights = () => {
                        if (this.reapplyHighlightsTimeout) {
                            window.clearTimeout(this.reapplyHighlightsTimeout);
                        }

                        this.reapplyHighlightsTimeout = window.setTimeout(
                            () =>
                                Object.keys(featureHighlightsMap).forEach(k => this.spatialUtilService.highlightFeatures(k, featureHighlightsMap[k])),
                            Math.max(1300, Math.min(5000, featureCount))
                        );
                    };

                    this.eventBus
                        .on('featuresUpdated')
                        .pipe(first())
                        .subscribe(() => reapplyHighlights());

                    reapplyHighlights();

                    if (this.mapService.getMapMode().type === 'mainPlan') {
                        this.mapService.resetMapServiceLayer(
                            newReferenceScaleLookup.divisor,
                            this.adjustFeatures,
                            this.adjustFeatures ? scaleRatio : 1
                        );
                    }

                    if (this.paperSizeId === oldPaperSizeLookupId) {
                        this.mapToolsService.deactivate('PRINT_AREA_WINDOW');
                    } else {
                        this.enlargementService.refresh(this.paperSizeId);
                    }
                })
            )
            .subscribe();
    }

    onClickCancel() {
        this.mapToolsService.deactivate('PRINT_AREA_WINDOW');
    }

    paperSizeChanged() {
        this.eventBus.cast('nlr:busy', this.setPrintAreaToDefaultExtent(true).pipe(first()).subscribe());
    }

    referenceScaleChanged() {
        this.updatePrintArea(this.extent, false);
    }

    private getSelectedReferenceScaleLookup() {
        return this.getReferenceScaleLookups().find(l => l.id === this.referenceScaleId);
    }

    private setPrintAreaToDefaultExtent(autoCalcScale: boolean) {
        const features = this.mapService.queryAllFeatures();

        const tenures = this.planModelService.getModel().formerTenures;
        autoCalcScale = autoCalcScale && (tenures.length > 0 || features.length > 0);
        return (autoCalcScale ? this.mapService.getDefaultExtent(tenures, features) : of(this.mapService.map.extent)).pipe(
            tap((extent: any) => this.updatePrintArea(extent, autoCalcScale))
        );
    }

    private getNextValidScaleId(scaleId: number) {
        // get next valid scale ID
        while (!this.printAreaService.isReferenceScaleIdValid(scaleId)) {
            scaleId++;
            // check max printable scale
            if (scaleId > 59) {
                scaleId = 59;
                break;
            }
        }
        return scaleId;
    }

    private updatePrintArea(extent: any, autoCalcScale: boolean) {
        this.extent = extent;

        if (this.mapGraphicsClearEvent) {
            this.mapGraphicsClearEvent.remove();
        }

        this.spatialUtilService.clearMapGraphics();

        let divisor = this.getSelectedReferenceScaleLookup().divisor;
        const qtdPerPage = 1;

        if (autoCalcScale) {
            const referenceScaleLookup = this.printAreaService.getReferenceScaleLookups().find(l => {
                const df = this.dataFrameService.getDataFrame(this.coordSystemId, this.paperSizeId, l.divisor, qtdPerPage, this.extent);
                const dfExtent = new this.esriModuleService.Extent(df.xmin, df.ymin, df.xmax, df.ymax, this.extent.spatialReference);
                return dfExtent.contains(new this.esriModuleService.Extent(this.extent).expand(1.1));
            });

            if (referenceScaleLookup) {
                this.referenceScaleId = referenceScaleLookup.id;
                divisor = referenceScaleLookup.divisor;
            }
        }

        const dataFrameExtent = this.dataFrameService.getDataFrame(this.coordSystemId, this.paperSizeId, divisor, qtdPerPage, this.extent);

        const polygon = new this.esriModuleService.geometry.Polygon(
            new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference())
        );
        polygon.addRing([
            [dataFrameExtent.xmin, dataFrameExtent.ymin],
            [dataFrameExtent.xmin, dataFrameExtent.ymax],
            [dataFrameExtent.xmax, dataFrameExtent.ymax],
            [dataFrameExtent.xmax, dataFrameExtent.ymin],
            [dataFrameExtent.xmin, dataFrameExtent.ymin]
        ]);

        const symbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_SOLID,
            new this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                new this.esriModuleService.Color([0, 112, 255, 0.25]),
                6
            ),
            new this.esriModuleService.Color([0, 112, 255, 0.25]),
            1
        );

        this.printAreaGraphic = new this.esriModuleService.Graphic(polygon, symbol, {type: 'printAreaGraphic'});

        this.spatialUtilService.addMapGraphic(null, this.printAreaGraphic);

        // Re-add print-graphic if it gets cleared by other tools (such as display preferences)
        this.mapGraphicsClearEvent = this.mapService.map.graphics.on('graphics-clear', () => {
            if (this.printAreaGraphic) {
                this.spatialUtilService.addMapGraphic(null, this.printAreaGraphic);
            }
        });
    }
}
